import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { USER_ASSETS } from "@/lib/monuments";

const EASE = [0.22, 1, 0.36, 1];
// Two-line hero: line 1 fits on one row at desktop; line 2 has "Life." in gold
const HERO_LINES = ["Bring India's History", "Back to Life."];

const HERO_TOP_IMAGE =
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_monument-reimagined/artifacts/x4heb50f_Screenshot%202026-07-22%20at%201.02.10%20PM.webp";

export default function Hero() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const topImgY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const topImgScale = useTransform(scrollYProgress, [0, 1], [1, 1.04]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.85], [1, 0.2]);

  const scrollToContact = (e) => {
    e.preventDefault();
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      ref={sectionRef}
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] pt-24 md:pt-28 pb-14 md:pb-20 overflow-hidden"
    >
      {/* Subtle background parallax layer */}
      <motion.div
        aria-hidden
        style={{ y: bgY, opacity: heroOpacity }}
        className="absolute inset-0 -z-10"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${USER_ASSETS.panoramic})`,
            filter: "brightness(0.24) saturate(1.08)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/60 via-obsidian/70 to-obsidian" />
        <div className="absolute inset-0 bg-[radial-gradient(1000px_500px_at_50%_18%,rgba(212,175,55,0.13),transparent_70%)]" />
      </motion.div>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 relative">
        {/* Feature image on top of the headline */}
        <motion.figure
          initial={{ opacity: 0, y: 30, clipPath: "inset(0 0 100% 0)" }}
          animate={{ opacity: 1, y: 0, clipPath: "inset(0 0 0% 0)" }}
          transition={{ duration: 1.3, ease: EASE, delay: 0.1 }}
          className="relative"
          data-testid="hero-top-image"
        >
          <div className="relative aspect-[21/6] md:aspect-[24/6] overflow-hidden border border-gold/20 bg-surface">
            <motion.img
              src={HERO_TOP_IMAGE}
              alt="Panoramic reawakening: from ruins through wireframe reconstruction to restored temples."
              style={{ y: topImgY, scale: topImgScale }}
              className="absolute inset-0 w-full h-full object-cover"
              loading="eager"
              draggable={false}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/40 via-transparent to-transparent" />
          </div>
        </motion.figure>

        {/* Eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.6 }}
          className="mt-8 md:mt-10 flex items-center gap-4 font-mono-accent text-[11px] text-gold/80"
          data-testid="hero-eyebrow"
        >
          <span className="w-8 h-px bg-gold/50" />
          A National Mission · Est. 2025
        </motion.div>

        {/* Compact 2-line headline — restored to original size with generous line-height */}
        <h1
          className="mt-4 font-display font-light tracking-[-0.02em] text-bone leading-[1.12] pb-2 text-[clamp(2.5rem,8.5vw,7.5rem)]"
          data-testid="hero-headline"
        >
          {HERO_LINES.map((line, i) => (
            <span key={i} className="hero-line-mask" style={{ paddingBottom: "0.1em" }}>
              <motion.span
                className="hero-line-inner whitespace-nowrap"
                initial={{ y: "110%", opacity: 0.2 }}
                animate={{ y: "0%", opacity: 1 }}
                transition={{ duration: 1.05, ease: EASE, delay: 0.75 + i * 0.14 }}
              >
                {i === 1 ? (
                  <>
                    Back to <em className="not-italic text-gold font-medium">Life.</em>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        {/* Subtext + CTA */}
        <div className="mt-8 grid md:grid-cols-12 gap-6 md:gap-8 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1, ease: EASE }}
            className="md:col-span-7 text-bone/75 text-lg md:text-xl leading-relaxed max-w-2xl"
            data-testid="hero-subtitle"
          >
            A civilisational overlay. We reconstruct India&rsquo;s monuments in
            VR to give you an immersive experience, not as ruins, but as living
            cities, ceremonies and skylines they once were.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.25, ease: EASE }}
            className="md:col-span-5 md:justify-self-end flex items-center gap-5"
          >
            <a
              href="#contact"
              onClick={scrollToContact}
              data-testid="hero-cta-contact"
              className="group inline-flex items-center gap-3 bg-gold text-obsidian px-7 py-4 rounded-full font-mono-accent text-[11px] hover:bg-bone transition-colors duration-300"
            >
              Contact us for enquiries
              <ArrowUpRight
                className="w-4 h-4 group-hover:rotate-45 transition-transform"
                strokeWidth={1.6}
              />
            </a>
            <a
              href="#paradox"
              data-testid="hero-cta-scroll"
              className="font-mono-accent text-[11px] text-bone/70 border-b border-bone/30 hover:text-gold hover:border-gold pb-1 transition-colors"
            >
              Scroll to explore
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
